import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, X, ChevronDown, MapPin, Check, Loader2 } from 'lucide-react';

/**
 * Normalizes text for searching (removes accents, trims, lowercase).
 */
const normalize = (text) => {
    if (!text) return "";
    return text
        .toString()
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""); // Remove accents
};

/**
 * CityCombobox — searchable city dropdown.
 * 
 * Controlled component that handles search with accent-insensitivity.
 * Uses index-based keys for the list items and a query-based key for the list
 * itself to ensure the DOM resets correctly on filter.
 */
const CityCombobox = ({
    cities = [],
    value,
    onChange,
    placeholder = 'Sélectionner une ville…',
    disabled = false,
    hasError = false,
    isLoading = false,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);

    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Selected city object
    const selected = useMemo(() =>
        cities.find(c => String(c.id) === String(value)) || null,
        [cities, value]
    );

    // Filtered list — calculated on every render for maximum reliability
    const q = normalize(query);
    const filtered = q
        ? cities.filter(c => normalize(c.name).includes(q))
        : cities;

    // Reset highlighted index when filtering or opening
    useEffect(() => {
        setHighlighted(0);
    }, [query, open]);

    // Sync scroll when highlighted changes
    useEffect(() => {
        if (!listRef.current) return;
        const item = listRef.current.querySelector(`[data-idx="${highlighted}"]`);
        if (item) {
            item.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        }
    }, [highlighted, query, open]);

    // Click outside handler
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleOpen = () => {
        if (disabled || isLoading) return;
        setQuery('');
        setOpen(true);
        // Focus input after render
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleSelect = useCallback((city) => {
        onChange(city.id, city.name);
        setOpen(false);
        setQuery('');
    }, [onChange]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(h => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlighted]) {
                handleSelect(filtered[highlighted]);
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
        }
    };

    const borderClass = hasError
        ? 'border-red-500 ring-2 ring-red-500/5'
        : open
            ? 'border-indigo-500 ring-2 ring-indigo-500/10 shadow-sm'
            : 'border-gray-100 hover:border-gray-200';

    return (
        <div ref={containerRef} className="relative w-full">
            {/* Trigger Button */}
            <button
                type="button"
                disabled={disabled || isLoading}
                onClick={handleOpen}
                className={`w-full flex items-center justify-between gap-2 pl-3 pr-3 py-2.5 rounded-xl bg-gray-50/50 border ${borderClass} focus:outline-none transition-all text-left ${(disabled || isLoading) ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
            >
                <div className="flex-1 truncate">
                    <span className={`text-sm font-bold ${selected ? 'text-gray-800' : 'text-gray-400 font-medium'}`}>
                        {isLoading ? 'Chargement des villes…' : (selected ? selected.name : placeholder)}
                    </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {isLoading ? (
                        <Loader2 size={14} className="animate-spin text-indigo-400" />
                    ) : (
                        <>
                            {selected && !disabled && !open && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); onChange('', ''); }}
                                    className="p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X size={12} />
                                </div>
                            )}
                            <ChevronDown
                                size={14}
                                className={`text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                            />
                        </>
                    )}
                </div>
            </button>

            {/* Dropdown Panel */}
            {open && (
                <div className="absolute z-[999] mt-2 w-full bg-white rounded-2xl border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-100">
                    {/* Search Field */}
                    <div className="p-2.5 border-b border-gray-50 bg-gray-50/30">
                        <div className="relative group">
                            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Rechercher une ville…"
                                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white border border-gray-100 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-gray-300"
                                autoComplete="off"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 p-1"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Scrollable List — KEYED BY QUERY to force full DOM reset on change */}
                    <ul
                        key={`list-${query}`}
                        ref={listRef}
                        className="max-h-[260px] overflow-y-auto py-1.5 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
                    >
                        {filtered.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-400 font-medium italic">
                                <Search size={24} className="mx-auto text-gray-100 mb-2" />
                                <p className="text-sm">Aucune ville pour "{query}"</p>
                            </div>
                        ) : (
                            filtered.map((city, idx) => {
                                const isSelected = String(city.id) === String(value);
                                const isHighlighted = idx === highlighted;
                                return (
                                    <li
                                        key={city.id}
                                        data-idx={idx}
                                        onMouseEnter={() => setHighlighted(idx)}
                                        onClick={() => handleSelect(city)}
                                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${isSelected
                                            ? 'bg-indigo-50 text-indigo-700'
                                            : isHighlighted
                                                ? 'bg-gray-50 text-gray-900 shadow-sm mx-1 rounded-lg'
                                                : 'text-gray-600'
                                            }`}
                                    >
                                        <MapPin size={12} className={isSelected ? 'text-indigo-400' : 'text-gray-300'} />
                                        <span className={`text-sm tracking-tight ${isSelected ? 'font-black' : 'font-bold'}`}>
                                            {city.name}
                                        </span>
                                        {isSelected && (
                                            <div className="ml-auto bg-indigo-500 rounded-full p-0.5">
                                                <Check size={10} className="text-white" />
                                            </div>
                                        )}
                                    </li>
                                );
                            })
                        )}
                    </ul>

                    {/* Footer Info */}
                    <div className="p-3 border-t border-gray-50 bg-gray-50/20 flex items-center justify-between">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            {query
                                ? `${filtered.length} résultat${filtered.length === 1 ? '' : 's'}`
                                : `${cities.length} villes`
                            }
                        </span>
                        {query && (
                            <span className="text-[9px] font-bold text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded uppercase">
                                Filtre Actif
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CityCombobox;
